import { useEffect, useState } from 'react'
import {
  acceptRecognitionProposal,
  dismissRecognitionProposal,
  getRecognitionOverview,
  proposeRecognitionUpdate,
  type AdminRecognitionOverview,
  type AdminRecognitionShowcase,
} from '../lib/api'

/**
 * Die Kamera lernt mit — und hier sieht man, was sie gelernt hat.
 *
 * Oben die Trefferquote je Showcase: wie oft erkannt, von welchem Modell, wie
 * oft der Besucher „Ja" oder „Nein" gesagt hat. Darunter die Vorschläge: Sätze,
 * die K4ia aus erfolgreichen Erkennungen verdichtet hat und die in die
 * visuellen Merkmale wandern — aber nur nach einem Klick auf „Übernehmen".
 * Kein Bild wird dafür gespeichert (FR-34); es sind ausschließlich Texte.
 */
export function RecognitionPage() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<AdminRecognitionOverview | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  async function refresh(tage = days) {
    setData(await getRecognitionOverview(tage))
  }

  useEffect(() => {
    void refresh(days)
  }, [days])

  async function run(id: string, aktion: () => Promise<unknown>) {
    setBusy(id)
    setMeldung(null)
    try {
      const ergebnis = (await aktion()) as { status?: string; hinweise?: number } | undefined
      if (ergebnis?.status === 'nichts-neues') {
        setMeldung(
          `K4ia hat ${ergebnis.hinweise} Beobachtungen gelesen und nichts gefunden, was in den Merkmalen fehlt.`,
        )
      }
      await refresh()
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  const offeneVorschlaege = data?.showcases.reduce((n, s) => n + s.proposals.length, 0) ?? 0
  const nichts = data ? data.none.loop + data.none.button + data.none.photo : 0

  return (
    <div className="max-w-4xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-2">
          <h2 className="page-title">Erkennung</h2>
          <p className="max-w-2xl text-sm leading-relaxed text-text-muted">
            Wie gut die Kamera die Stände erkennt — und was K4ia daraus gelernt hat. Gespeichert
            werden nur Ergebnisse und Sätze, nie Bilder. Vorschläge ändern die Merkmale erst, wenn
            du sie übernimmst.
          </p>
        </div>
        <label className="flex w-full items-center gap-3 text-sm text-text-muted sm:w-auto">
          Zeitraum
          <select
            className="input sm:w-auto"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
      </div>

      {meldung && (
        <p role="status" className="text-sm text-text-muted">
          {meldung}
        </p>
      )}

      {data && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Kachel
            wert={data.showcases.reduce((n, s) => n + s.recognized, 0)}
            label="Erkennungen"
            hint={`${nichts} Bilder ohne Treffer, ${data.groups}× die ganze Vitrine`}
          />
          <Kachel
            wert={data.showcases.reduce((n, s) => n + s.newHints, 0)}
            label="Neue Beobachtungen"
            hint="Sätze aus erfolgreichen Erkennungen, noch nicht verdichtet"
          />
          <Kachel
            wert={offeneVorschlaege}
            label="Offene Vorschläge"
            hint="Warten auf Übernehmen oder Verwerfen"
          />
        </div>
      )}

      <section className="space-y-3">
        <h3 className="section-title">Je Showcase</h3>
        {/* Am Handy je Showcase eine Karte — die Tabelle hat dort keinen Platz. */}
        <ul className="space-y-3 md:hidden">
          {data?.showcases.map((s) => (
            <li key={s.showcaseId} className="card space-y-3 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{s.title}</span>
                {!s.visualDescription.trim() && (
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-status-error">
                    ohne Merkmale
                  </span>
                )}
              </div>
              <dl className="grid grid-cols-4 gap-2 text-center">
                <Zahl label="Erkannt" wert={String(s.recognized)} />
                <Zahl label="klein / LLM" wert={`${s.bySmall} / ${s.byLlm}`} />
                <Zahl label="Ja / Nein" wert={`${s.confirmed} / ${s.declined}`} />
                <Zahl label="Beobacht." wert={String(s.newHints)} />
              </dl>
              {s.newHints > 0 && (
                <button
                  type="button"
                  className="btn-secondary w-full"
                  disabled={busy !== null}
                  onClick={() =>
                    void run(s.showcaseId, () => proposeRecognitionUpdate(s.showcaseId))
                  }
                >
                  {busy === s.showcaseId ? 'Denkt nach …' : 'Vorschlag erzeugen'}
                </button>
              )}
            </li>
          ))}
        </ul>
        <div className="card hidden overflow-hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-4 py-2">Showcase</th>
                <th className="px-2 py-2 text-right">Erkannt</th>
                <th className="px-2 py-2 text-right">klein / LLM</th>
                <th className="px-2 py-2 text-right">Ja / Nein</th>
                <th className="px-2 py-2 text-right">Beobacht.</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data?.showcases.map((s) => (
                <tr key={s.showcaseId} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">
                    <span className="font-medium">{s.title}</span>
                    {!s.visualDescription.trim() && (
                      <span className="ml-2 rounded-full bg-surface-2 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-status-error">
                        ohne Merkmale
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{s.recognized}</td>
                  <td className="px-2 py-2 text-right font-mono text-text-muted">
                    {s.bySmall} / {s.byLlm}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-text-muted">
                    {s.confirmed} / {s.declined}
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{s.newHints}</td>
                  <td className="px-4 py-2 text-right">
                    {s.newHints > 0 && (
                      <button
                        type="button"
                        className="text-brand"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(s.showcaseId, () => proposeRecognitionUpdate(s.showcaseId))
                        }
                      >
                        {busy === s.showcaseId ? 'Denkt nach …' : 'Vorschlag erzeugen'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-1 text-xs leading-relaxed text-text-muted">
          „klein“ ist das schnelle Bildmodell der Kameraschleife, „LLM“ das Sprachmodell, das
          nachschaut, wenn das kleine nichts sieht. „Ja / Nein“ zählt, wie Besucher auf den
          Vorschlag der Kamera reagiert haben. Vorschläge entstehen nachts von selbst, sobald drei
          Beobachtungen vorliegen — oder hier auf Knopfdruck.
        </p>
      </section>

      {data?.showcases.some((s) => s.proposals.length > 0) && (
        <section className="space-y-3">
          <h3 className="section-title">Vorschläge</h3>
          {data.showcases
            .filter((s) => s.proposals.length > 0)
            .map((s) => (
              <Vorschlaege
                key={s.showcaseId}
                showcase={s}
                busy={busy}
                onAccept={(id) => void run(id, () => acceptRecognitionProposal(id))}
                onDismiss={(id) => void run(id, () => dismissRecognitionProposal(id))}
              />
            ))}
        </section>
      )}
    </div>
  )
}

function Zahl({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex flex-col-reverse rounded-xl bg-surface-2 px-1 py-2">
      <dt className="text-[0.65rem] leading-tight text-text-muted">{label}</dt>
      <dd className="font-mono text-sm">{wert}</dd>
    </div>
  )
}

function Kachel({ wert, label, hint }: { wert: number; label: string; hint: string }) {
  return (
    <div className="card flex items-center gap-4 p-4 sm:block">
      <p className="w-12 shrink-0 text-center text-3xl font-semibold tabular-nums sm:w-auto sm:text-left">
        {wert}
      </p>
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-text-muted">{hint}</p>
      </div>
    </div>
  )
}

function Vorschlaege({
  showcase,
  busy,
  onAccept,
  onDismiss,
}: {
  showcase: AdminRecognitionShowcase
  busy: string | null
  onAccept: (id: string) => void
  onDismiss: (id: string) => void
}) {
  return (
    <div className="card divide-y divide-border overflow-hidden">
      <div className="px-5 py-3">
        <p className="font-medium">{showcase.title}</p>
        <p className="text-xs text-text-muted">
          Bisherige Merkmale: {showcase.visualDescription.trim() || '— noch keine —'}
        </p>
      </div>
      {showcase.proposals.map((p) => (
        <div key={p.id} className="space-y-3 px-5 py-4">
          <p className="text-xs text-text-muted">
            Vorschlag vom {new Date(p.createdAt).toLocaleString('de-DE')}
          </p>
          <p className="text-sm leading-relaxed">{p.text}</p>
          <div className="flex gap-3">
            <button
              type="button"
              className="btn-primary flex-1 sm:flex-none"
              disabled={busy !== null}
              onClick={() => onAccept(p.id)}
            >
              Übernehmen
            </button>
            <button
              type="button"
              className="btn-danger flex-1 sm:flex-none"
              disabled={busy !== null}
              onClick={() => onDismiss(p.id)}
            >
              Verwerfen
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
